import { Formik, Form, Field } from "formik";
import { FormModel } from "../types/FormTypes";
import { useAtom } from "jotai";
import * as Yup from "yup";
import { formDataAtom, travelSearchAtom, selectedJourneyAtom } from "../App";
import { useNavigate } from "react-router-dom";
import { useRef, useState, useEffect } from "react";
import Box from "../components/Box/Box";
import Progressbar from "../components/Progressbar/Progressbar";
import ErrorList from "../components/Form/ErrorList";
import { handleSubmitClick } from "../components/Form/FormUtils";
import BackButton from "../components/Buttons/BackButton";
import CustomAutocomplete from "../components/Form/AutoComplete";
import { useDebounce } from "use-debounce";
import dayjs from 'dayjs';
import localeData from 'dayjs/plugin/localeData';
import updateLocale from 'dayjs/plugin/updateLocale';
import 'dayjs/locale/sv';
import useFocusOnLoad from "../hooks/useFocusOnLoad";
import DatePicker from "../components/Form/Datepicker";
import InputText from "../components/Form/InputText";

interface Stop {
    PlaceName: string;
    OgtType: string;
    Ll: [number, number];
    Id: number;
}
interface Ogt {
    OgtTrafficTypeGroupCssClass: string;
    OgtTrafficTypeIconCssClass: string;
    OgtTrafficTypeIconReplaceLineName: boolean;
    OgtLineNamePreformatted: string;
}

interface Line {
    LineName: string,
    LineTypeId: number,
    LineTypeName: string,
    IsWalk: boolean,
    Ogt: Ogt,
}
interface RouteLink {
    Line: Line,
}
interface Journeys {
    Arrival: string;
    Departure: string;
    JourneyKey: string;
    TravelTime: string;
    Switch: string;
    RouteLinks: RouteLink[] | null,
}

export default function TravelSearch() {
    const apiBaseUrl = process.env.REACT_APP_TPS_API_BASE_URL?.replace(/\/$/, '') ?? '';

    const h2Ref = useFocusOnLoad<HTMLHeadingElement>();

    const navigate = useNavigate();
    const formRef = useRef<HTMLFormElement>(null);
    const searchButtonRef = useRef<HTMLButtonElement | null>(null);

    const [formData, setFormData] = useAtom(formDataAtom);
    const [travelSearch, setTravelSearch] = useAtom(travelSearchAtom);
    const [selectedJourney, setSelectedJourney] = useAtom(selectedJourneyAtom);

    const [searchErrors, setSearchErrors] = useState({
        journeyFromError: '',
        journeyToError: '',
        journeyTimeError: '',
        journeyDateError: '',
        journeyFormError: '',
        formError: '',
    });

    const [fromQuery, setFromQuery] = useState<string>('');
    const [toQuery, setToQuery] = useState<string>('');
    const [fromResults, setFromResults] = useState<Stop[]>([]);
    const [toResults, setToResults] = useState<Stop[]>([]);
    const [searchResults, setSearchResults] = useState<Journeys[]>([]);

    const [searchType, setSearchType] = useState<"from" | "to" | null>(null);

    const [debouncedFromQuery] = useDebounce(fromQuery, 300);
    const [debouncedToQuery] = useDebounce(toQuery, 300);

    dayjs.extend(localeData);
    dayjs.extend(updateLocale);

    dayjs.updateLocale('sv', {
        weekdays: [
            "Söndag", "Måndag", "Tisdag", "Onsdag", "Torsdag", "Fredag", "Lördag"
        ],
        months: [
            "januari", "februari", "mars", "april", "maj", "juni",
            "juli", "augusti", "september", "oktober", "november", "december"
        ]
    });

    useEffect(() => {
        if (travelSearch.selectedJourneyFrom && travelSearch.selectedJourneyTo && travelSearch.journeyDate && travelSearch.journeyTime) {
            doTravelSearch();

            setFromQuery(travelSearch.selectedJourneyFrom.PlaceName);
            setToQuery(travelSearch.selectedJourneyTo.PlaceName);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!searchType) return;

        const query = searchType === "from" ? debouncedFromQuery : debouncedToQuery;
        const trimmed = (query ?? "").trim();
        if (trimmed.length < 2) {
            if (searchType === "from") setFromResults([]);
            else setToResults([]);
            return;
        }

        const params = new URLSearchParams();
        params.set("q", trimmed); // only q

        fetch(`${apiBaseUrl}/api/v1/Points/Find?${params.toString()}`)
            .then(async (res) => {
                const text = await res.text();
                if (!res.ok) {
                    console.warn("Points/Find bad response:", res.status, text);
                    if (searchType === "from") setFromResults([]);
                    else setToResults([]);
                    return null;
                }
                return JSON.parse(text);
            })
            .then((data) => {
                if (!data) return;
                const arr: Stop[] = Array.isArray(data) ? data : [];
                if (searchType === "from") setFromResults(arr);
                else setToResults(arr);
            })
            .catch((err) => {
                console.error("Points/Find fetch error:", err);
                if (searchType === "from") setFromResults([]);
                else setToResults([]);
            });
    }, [debouncedFromQuery, debouncedToQuery, searchType, apiBaseUrl]);





    const searchFormRef = useRef<HTMLDivElement>(null);
    const [isFocused, setIsFocused] = useState(false);

    useEffect(() => {
        const handleFocus = () => {
            if (searchFormRef.current) {
                const isActive = searchFormRef.current.contains(document.activeElement);
                setIsFocused(isActive);
            }
        };
        document.addEventListener("focusin", handleFocus);
        document.addEventListener("focusout", handleFocus);

        return () => {
            document.removeEventListener("focusin", handleFocus);
            document.removeEventListener("focusout", handleFocus);
        };
    }, []);

    const handleSelect = (placeName: string, type: "from" | "to") => {
        const list = type === "from" ? fromResults : toResults;
        const safeList: Stop[] = Array.isArray(list) ? list : [];
        const selectedItem = safeList.find((item) => item.PlaceName === placeName) || null;

        if (type === "from") {
            setTravelSearch((prev) => ({ ...prev, selectedJourneyFrom: selectedItem }));
            setFromQuery(selectedItem?.PlaceName ?? "");
            setFromResults([]);
        } else {
            setTravelSearch((prev) => ({ ...prev, selectedJourneyTo: selectedItem }));
            setToQuery(selectedItem?.PlaceName ?? "");
            setToResults([]);
        }
    };

    const handleQueryChange = (value: string, type: "from" | "to") => {
        if (type === "from") {
            setFromQuery(value);
            if (value.trim() === '') {
                setTravelSearch(p => ({ ...p, selectedJourneyFrom: null }));
                setFromResults([]);
            }
        } else {
            setToQuery(value);
            if (value.trim() === '') {
                setTravelSearch(p => ({ ...p, selectedJourneyTo: null }));
                setToResults([]);
            }
        }
        setSearchType(type);
    };



    const handleTravelSearchChange = (field: keyof typeof travelSearch, value: string) => {

        setTravelSearch((prev) => ({
            ...prev,
            [field]: value,
        }));
    };

    const handleKeyDown = (event: React.KeyboardEvent) => {
        if (event.key === 'Enter') {
            doTravelSearch(true);
        }
    };

    const formatTimeInput = (value: string) => {
        const cleanedInput = value.replace(/\D/g, '');
        let newTime = '00:00';

        if (cleanedInput.length === 1) {
            newTime = `0${cleanedInput}:00`;
        } else if (cleanedInput.length === 2) {
            const hours = cleanedInput;
            newTime = `${hours === '24' ? '00' : hours}:00`;
        } else if (cleanedInput.length === 3) {
            const hours = cleanedInput[0] === '2' && cleanedInput[1] === '4' ? '00' : `0${cleanedInput[0]}`;
            const minutes = cleanedInput.slice(1);
            newTime = `${hours}:${minutes}`;
        } else if (cleanedInput.length >= 4) {
            let hours = cleanedInput.slice(0, 2);
            const minutes = cleanedInput.slice(2, 4);
            if (hours === '24') {
                hours = '00';
            }
            newTime = `${hours}:${minutes}`;
        }

        setTravelSearch((prev) => ({
            ...prev,
            journeyTime: newTime,
        }));

        return newTime;
    };


    const doTravelSearch = (clearSelectedJourney: boolean = false) => {
        const errors = { journeyFromError: '', journeyToError: '', journeyDateError: '', journeyTimeError: '' };

        if (!travelSearch.selectedJourneyFrom?.PlaceName.length || travelSearch.selectedJourneyFrom.PlaceName.length < 1) {
            errors.journeyFromError = 'Vänligen fyll i en avreseplats';
        }

        if (!travelSearch.selectedJourneyTo?.PlaceName.length || travelSearch.selectedJourneyTo.PlaceName.length < 1) {
            errors.journeyToError = 'Vänligen fyll i en destination';
        }

        if (travelSearch.journeyDate.length < 1) {
            errors.journeyDateError = 'Vänligen fyll i ett datum';
        }

        if (travelSearch.journeyTime.length < 1) {
            errors.journeyTimeError = 'Vänligen fyll i en tid';;
        }

        const today = dayjs().startOf('day');
        const selectedDate = dayjs(travelSearch.journeyDate);

        if (selectedDate.isAfter(today)) {
            errors.journeyDateError = 'Vänligen ange ett datum som inte har passerat';
        }

        setSearchErrors(prevErrors => ({
            ...prevErrors,
            ...errors,
        }));


        const hasErrors = Object.values(errors).some((error) => error.trim().length > 0);

        if (hasErrors) {
            setSearchResults([]);
            setFormData((prev) => ({
                ...prev,
                journey: '',
            }));
            return;
        }

        if (clearSelectedJourney) {
            setSelectedJourney({
                fromName: '',
                toName: '',
                date: '',
                travelTime: '',
                journeyKey: '',
                line: '',
                switches: '',
                unformattedDate: '',
            });

            setFormData((prev) => ({
                ...prev,
                journey: '',
                selectedJourney,
            }));
        }

        const params = {
            startId: travelSearch.selectedJourneyFrom?.Id,
            endId: travelSearch.selectedJourneyTo?.Id,
            startType: travelSearch.selectedJourneyFrom?.OgtType,
            endType: travelSearch.selectedJourneyTo?.OgtType,
            startLl: `${travelSearch.selectedJourneyFrom?.Ll[0]},${travelSearch.selectedJourneyFrom?.Ll[1]}`,
            endLl: `${travelSearch.selectedJourneyTo?.Ll[0]},${travelSearch.selectedJourneyTo?.Ll[1]}`,
            startName: travelSearch.selectedJourneyFrom?.PlaceName,
            endName: travelSearch.selectedJourneyTo?.PlaceName,
            date: travelSearch.journeyDate,
            time: formatTimeInput(travelSearch.journeyTime),
            direction: 0,
            span: 'Default',
            trafficTypes: 0,
            changetime: 'Normal',
            priority: 'ShortestTime',
            walk: true
        };

        const searchParams = new URLSearchParams(
            Object.fromEntries(Object.entries(params).map(([key, value]) => [key, String(value)]))
        );

        fetch(`${apiBaseUrl}/api/v1/Journeys/Find?${searchParams.toString()}`)
            .then((response) => response.json())
            .then((data) => {
                let errorText = '';
                setSearchResults(data.Journeys || []);
                if (data.ErrorText && data.ErrorText.includes('JourneyDateTime')) {
                    errorText = 'Vänligen ange en giltig tid'
                } else {
                    errorText = data.ErrorText;
                }
                setSearchErrors({
                    journeyFromError: '',
                    journeyToError: '',
                    journeyTimeError: '',
                    journeyDateError: '',
                    journeyFormError: errorText || '',
                    formError: '',
                });
            })
            .catch((error) => {
                console.error("Error:", error);

            });
    };

    const formatSelectedJourney = (journey: any) => {

        const lineNames = journey.RouteLinks
            .map((routeLink: any) => {
                const lineName = routeLink.Line.LineName;
                const trainNo = routeLink.Line.TrainNo;
                return trainNo !== 0 ? `${lineName} (#${trainNo})` : lineName;
            })
            .join(", ");

        const formattedJourney = {
            fromName: journey.UsedStartPlace.PlaceName,
            toName: journey.UsedEndPlace.PlaceName,
            date: formatDate(journey.Departure, 'dddd D MMMM'),
            travelTime: `${formatDate(journey.Departure, 'HH:mm')} - ${formatDate(journey.Arrival, 'HH:mm')}`,
            journeyKey: journey.JourneyKey,
            line: lineNames,
            switches: journey.Switch,
            unformattedDate: journey.Departure,
        }
        return formattedJourney;
    };

    const formatDate = (date: string, format: string) => {
        const formattedDate = dayjs(date).locale('sv').format(format);
        return formattedDate;
    };

    const groupResultsByDate = (results: Journeys[]) => {
        return results.reduce((acc: Record<string, Journeys[]>, result) => {
            const date = formatDate(result.Departure, "dddd D MMMM");
            if (!acc[date]) {
                acc[date] = [];
            }
            acc[date].push(result);
            return acc;
        }, {});
    };

    const updateFormData = (value: string, result: any) => {
        setSelectedJourney(formatSelectedJourney(result));
    }

    return (
        <div className="travel-search-wrapper">
            <Formik<FormModel>
                onSubmit={(values) => {
                    setFormData({
                        ...formData,
                        ...values,
                        selectedJourney
                    });
                    navigate("/hur-sen-blev-du");
                }}
                initialValues={{
                    ...formData,
                }}
                enableReinitialize
                validateOnChange={false}
                validateOnBlur={false}
                validationSchema={Yup.object().shape({
                    journey: Yup.string().test(
                        "at-least-one-radio-selected",
                        "Vänligen välj en resa för att gå vidare till nästa steg",
                        (value) => {
                            return searchResults.some((result) => result.JourneyKey === value);
                        }
                    ),
                })}
            >
                {({ errors, values }) => (
                    <>
                        <div className="flex justify-end items-center">
                            <div className="xs:w-52 md:w-full max-w-xs">
                                <Progressbar percentage={"10"} />
                            </div>
                        </div>
                        <Form ref={formRef} noValidate>
                            <Box fullwidth={true}>

                                <h2 ref={h2Ref} id="TravelSearchHeading" tabIndex={-1}>Hur såg din planerade resa ut?</h2>

                                <p>
                                    Vi börjar med vilken avgång det gäller.
                                    Var åkte du ifrån, vart skulle du och när var det? Fyll i uppgifterna här för att få förslag på resor.
                                </p>

                                {searchErrors.journeyFormError.length > 0 && (
                                    <span className="validation-error">{searchErrors.journeyFormError}</span>
                                )}
                                <div className="travel-search-form" ref={searchFormRef}>
                                    <CustomAutocomplete
                                        id="journeyFrom"
                                        label="Från"
                                        errorMessage={searchErrors.journeyFromError}
                                        value={fromQuery !== undefined ? fromQuery : travelSearch.selectedJourneyFrom?.PlaceName || ''}
                                        onChange={(e: any) => {
                                            setFromQuery(e.target.value);
                                            setSearchType("from");
                                        }}
                                        onQueryChange={handleQueryChange}
                                        options={fromResults}
                                        onSelect={(placeName: any) => handleSelect(placeName, "from")}
                                        type="from"
                                        onKeyDown={handleKeyDown}
                                    />

                                    <CustomAutocomplete
                                        id="journeyTo"
                                        label="Till"
                                        errorMessage={searchErrors.journeyToError}
                                        value={toQuery !== undefined ? toQuery : travelSearch.selectedJourneyTo?.PlaceName || ''}
                                        onChange={(e: any) => {
                                            setToQuery(e.target.value);
                                            setSearchType("to");
                                        }}
                                        onQueryChange={handleQueryChange}
                                        options={toResults}
                                        onSelect={(placeName: any) => handleSelect(placeName, "to")}
                                        type="to"
                                        onKeyDown={handleKeyDown}
                                    />

                                    <DatePicker
                                        name="journey_date"
                                        label={
                                            <>
                                                Datum för resa <span>(Obligatoriskt)</span>
                                            </>
                                        }
                                        value={travelSearch.journeyDate}
                                        onChange={(value) => handleTravelSearchChange('journeyDate', value)}
                                        error={searchErrors.journeyDateError}
                                        maxDate={new Date().toISOString().split('T')[0]}
                                        onKeyDown={handleKeyDown}
                                        disableFutureDates
                                    />

                                    <div className="input-container">
                                        <label htmlFor="journey_time">
                                            <span>Ungefärlig avgångstid <span>(Obligatoriskt)</span></span>
                                            {searchErrors.journeyTimeError.length > 0 && (
                                                <span className="validation-error">{searchErrors.journeyTimeError}</span>
                                            )}
                                        </label>
                                        <input type="text" id="journey_time" name="journey_time"
                                            value={travelSearch.journeyTime || ''}
                                            onChange={(e) => handleTravelSearchChange('journeyTime', e.target.value)}
                                            onKeyDown={handleKeyDown}
                                            className={`${searchErrors.journeyTimeError ? "has-error" : ""} `}
                                        />
                                    </div>
                                </div>

                                <button
                                    onClick={(e) => doTravelSearch(true)}
                                    className="btn primary travel-search-button"
                                    type="button"
                                    id="searchButton"
                                    ref={searchButtonRef}
                                >
                                    Sök resa
                                </button>

                                {Object.keys(errors).length > 0 && <ErrorList errors={errors} />}

                                {searchResults.length > 0 && (
                                    <div className="search-results-container">
                                        {Object.entries(groupResultsByDate(searchResults)).map(([date, results]) => (
                                            <div key={date}>
                                                {errors.journey && (
                                                    <span
                                                        aria-describedby="forMe forOther"
                                                        className="validation-error"
                                                    >
                                                        {errors.journey.toString()}
                                                    </span>
                                                )}
                                                <h3>
                                                    {date}, {results.length} {results.length > 1 ? 'resor' : 'resa'}
                                                </h3>
                                                <div
                                                    role="radiogroup"
                                                    aria-labelledby={`search-results-${date}`}
                                                    className={`radio-group travel-search-results ${errors.journey ? " has-error" : ""}`}

                                                >

                                                    {results
                                                        .filter((item, index, self) =>
                                                            index === self.findIndex((t) => t.JourneyKey === item.JourneyKey)
                                                        )
                                                        .map((result) => (
                                                            <div key={result.JourneyKey} className={result.JourneyKey === values.journey ? 'checked' : ''}>
                                                                <Field
                                                                    id={result.JourneyKey}
                                                                    type="radio"
                                                                    name="journey"
                                                                    value={result.JourneyKey}
                                                                    checked={values.journey === result.JourneyKey}
                                                                    onClick={(e: any) => updateFormData(e.target.value, result)}
                                                                />

                                                                <label htmlFor={result.JourneyKey}>
                                                                    <span>
                                                                        <span className="travel-departure-arrival">
                                                                            {formatDate(result.Departure, "HH:mm")} - {" "}
                                                                            {formatDate(result.Arrival, "HH:mm")}
                                                                        </span>
                                                                        <span className="travel-time">
                                                                            {result.TravelTime}
                                                                        </span>
                                                                        <span className="switch">
                                                                            {result.Switch} {parseInt(result.Switch) === 1 ? "byte" : "byten"}
                                                                        </span>
                                                                        <span className="lineicon-container">
                                                                            {result.RouteLinks && Array.isArray(result.RouteLinks) && result.RouteLinks.map((item, index) => (
                                                                                <span
                                                                                    className={`lineicon ${item.Line.LineTypeName} ${item.Line.Ogt.OgtTrafficTypeGroupCssClass} ${item.Line.LineName.includes('Östgötapendeln') ? 'pendeln' : ''}`}
                                                                                    key={index}
                                                                                >
                                                                                    {item.Line.Ogt.OgtTrafficTypeIconReplaceLineName ? (
                                                                                        <i
                                                                                            className={`traffic-icon--graphic ${item.Line.Ogt.OgtTrafficTypeIconCssClass}-32`} aria-hidden="true">
                                                                                        </i>
                                                                                    ) : (
                                                                                        item.Line.LineName
                                                                                    )}
                                                                                </span>
                                                                            ))}
                                                                        </span>
                                                                    </span>
                                                                </label>
                                                            </div>
                                                        ))}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </Box>

                            <div className="flex justify-center">
                                <div className="w-full text-left flex items-center">
                                    <BackButton />
                                </div>
                                <div className="w-full text-right">
                                    {searchResults.length > 0 && (
                                        <button
                                            onClick={handleSubmitClick(formRef)}
                                            className="btn primary"
                                            disabled={isFocused}
                                            type="submit"
                                        >
                                            Gå vidare
                                        </button>
                                    )}
                                </div>
                            </div>

                        </Form>
                    </>
                )}
            </Formik>
        </div>
    );
}
